/**
 * Verification Dashboard Component
 * Main dashboard showing verification status, documents, and benefits
 */

import React, { useState, useEffect } from 'react';
import {
    PlusIcon,
    EyeIcon,
    TrashIcon,
    DocumentArrowUpIcon,
    CheckBadgeIcon,
    GiftIcon,
    ChartBarIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useVerificationStatus } from '../../hooks/useVerificationStatus';
import VerificationStatusChip from './VerificationStatusChip';
import TrustScoreDisplay from './TrustScoreDisplay';
import DocumentUploadModal from './DocumentUploadModal';
import DocumentViewer from './DocumentViewer';
import VerificationBenefits from './VerificationBenefits';
import { DOCUMENT_CONFIGS } from '../../constants/verification';
import { DocumentVerification } from '../../types/verification';
import verificationService from '../../services/verificationService';

interface VerificationDashboardProps {
    className?: string;
}

const VerificationDashboard: React.FC<VerificationDashboardProps> = ({
    className = ''
}) => {
    // All hooks must be declared unconditionally at the top
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'benefits' | 'history'>('overview');
    const [deleteConfirm, setDeleteConfirm] = useState<{
        isOpen: boolean;
        documentId?: string;
        documentName?: string;
    }>({ isOpen: false });
    const [deletingDocument, setDeletingDocument] = useState<string | null>(null);
    const [documentViewer, setDocumentViewer] = useState<{
        isOpen: boolean;
        document: any;
    }>({ isOpen: false, document: null });
    const [loadingDocument, setLoadingDocument] = useState<string | null>(null);

    const {
        data,
        trustHistory,
        isLoading,
        error,
        refreshing,
        refresh,
        verificationProgress,
        missingRequiredDocuments,
        isFullyVerified
    } = useVerificationStatus();

    // Note: Socket integration is now handled entirely by the useVerificationStatus hook
    // to prevent duplicate subscriptions and ensure data consistency

    // Listen for verification status changes (legacy event system)
    useEffect(() => {
        const handleVerificationStatusChange = () => {
            refresh();
        };

        window.addEventListener('verificationStatusChanged', handleVerificationStatusChange);

        return () => {
            window.removeEventListener('verificationStatusChanged', handleVerificationStatusChange);
        };
    }, [refresh]);

    // Delete document handlers
    const handleDeleteDocument = (doc: DocumentVerification) => {
        // Only allow deletion of rejected or expired documents
        if (!['rejected', 'expired'].includes(doc.status)) {
            return;
        }

        const formattedDoc = formatDocument(doc);
        setDeleteConfirm({
            isOpen: true,
            documentId: doc.id,
            documentName: formattedDoc.displayName
        });
    };

    const confirmDeleteDocument = async () => {
        if (!deleteConfirm.documentId) return;

        setDeletingDocument(deleteConfirm.documentId);

        try {
            const result = await verificationService.deleteDocument(deleteConfirm.documentId);

            // Always refresh the verification status to update the UI
            await refresh();

            // Show success message
            console.log(result.message || 'Document deleted successfully');

        } catch (error: any) {
            console.error('Failed to delete document:', error);

            // If it's a 404 error (document already deleted), still refresh
            if (error.response?.status === 404 ||
                error.response?.data?.message?.includes('already deleted')) {
                await refresh();
                console.log('Document was already deleted, refreshing list');
            }
            // Show error message for other errors
        } finally {
            setDeletingDocument(null);
            setDeleteConfirm({ isOpen: false });
        }
    };

    // View document handlers
    const handleViewDocument = async (doc: DocumentVerification) => {
        setLoadingDocument(doc.id);

        try {
            const response = await verificationService.getDocumentForViewing(doc.id);

            if (response.success && response.data) {
                setDocumentViewer({
                    isOpen: true,
                    document: response.data
                });
            } else {
                console.error('Failed to load document for viewing');
            }
        } catch (error: any) {
            console.error('Error loading document for viewing:', error);
            // Could show a toast notification here
        } finally {
            setLoadingDocument(null);
        }
    };

    const handleCloseDocumentViewer = () => {
        setDocumentViewer({ isOpen: false, document: null });
    };

    const handleDownloadDocument = async () => {
        if (!documentViewer.document) return;

        try {
            const blob = await verificationService.downloadDocument(documentViewer.document.id);

            // Create download link
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = documentViewer.document.fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('Error downloading document:', error);
            // Could show a toast notification here
        }
    };

    const cancelDeleteDocument = () => {
        setDeleteConfirm({ isOpen: false });
    };

    // Early returns happen AFTER all hooks are declared
    if (isLoading) {
        return (
            <div className={`card p-6 ${className}`}>
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-neutral-200 dark:bg-neutral-700 rounded w-1/3"></div>
                    <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/2"></div>
                    <div className="h-32 bg-neutral-200 dark:bg-neutral-700 rounded"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`card p-6 ${className}`}>
                <div className="text-center py-8">
                    <div className="text-red-600 dark:text-red-400 mb-4">
                        Failed to load verification status
                    </div>
                    <button
                        onClick={() => refresh()}
                        className="btn-primary"
                        disabled={refreshing}
                    >
                        {refreshing ? 'Retrying...' : 'Try Again'}
                    </button>
                </div>
            </div>
        );
    }

    if (!data) return null;

    // Ensure documents array exists
    if (!data.documents) {
        data.documents = [];
    }

    // Format document for display with fallback for unknown document types
    const formatDocument = (doc: DocumentVerification) => {
        const config = DOCUMENT_CONFIGS[doc.documentType];

        // Handle case where document type is not found in config
        if (!config) {
            console.warn(`Unknown document type: ${doc.documentType}`);
            const fileSize = doc.fileSize && doc.fileSize > 0
                ? (doc.fileSize / 1024 / 1024).toFixed(2)
                : '0.00';
            return {
                ...doc,
                displayName: doc.documentType?.replace(/_/g, ' ') || 'Unknown Document',
                category: 'other',
                fileSizeMB: `${fileSize} MB`,
                uploadedDate: new Date(doc.createdAt).toLocaleDateString()
            };
        }

        const fileSize = doc.fileSize && doc.fileSize > 0
            ? (doc.fileSize / 1024 / 1024).toFixed(2)
            : '0.00';

        return {
            ...doc,
            displayName: config.displayName,
            category: config.category,
            fileSizeMB: `${fileSize} MB`,
            uploadedDate: new Date(doc.createdAt).toLocaleDateString()
        };
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: ChartBarIcon },
        { id: 'documents', label: 'Documents', icon: DocumentArrowUpIcon },
        { id: 'benefits', label: 'Benefits', icon: GiftIcon },
        { id: 'history', label: 'History', icon: EyeIcon }
    ];

    return (
        <div className={`space-y-6 ${className}`}>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                    <h2 className="text-2xl sm:text-3xl md:heading-section">Verification Center</h2>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 sm:mt-3">
                        <p className="text-base sm:text-lg md:text-body">
                            Manage your account verification and trust score
                        </p>
                        {/* Show verification status details */}
                        {data.documents && data.documents.length > 0 && (
                            <div className="flex items-center gap-2 text-xs sm:text-sm">
                                <span className="text-neutral-600 dark:text-neutral-400">
                                    Documents: {data.documents.length}
                                </span>
                                {data.documents.some(doc => doc.status === 'rejected') && (
                                    <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs sm:text-sm">
                                        Action Required
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <VerificationStatusChip status={data.status} size="lg" />

                    {data.canUploadMore && (
                        <button
                            onClick={() => setShowUploadModal(true)}
                            disabled={data.documents.some(doc => doc.status === 'rejected')}
                            className={`flex items-center gap-2 ${data.documents.some(doc => doc.status === 'rejected')
                                ? 'btn-secondary opacity-50 cursor-not-allowed'
                                : 'btn-primary'
                                }`}
                            title={data.documents.some(doc => doc.status === 'rejected')
                                ? 'Please delete rejected documents before uploading new ones'
                                : 'Upload new documents'
                            }
                        >
                            <PlusIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">Upload Documents</span>
                            <span className="sm:hidden">Upload</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <TrustScoreDisplay
                    currentScore={data.trustSystem.currentScore}
                    level={data.trustSystem.level}
                    showProgress={true}
                />

                <div className="card p-4 sm:p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-4 sm:mb-5">
                        <DocumentArrowUpIcon className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                        <span className="font-medium text-sm sm:text-base text-neutral-900 dark:text-neutral-100">
                            Verification Progress
                        </span>
                    </div>

                    <div className="space-y-3 sm:space-y-4">
                        <div className="flex justify-between text-xs sm:text-sm">
                            <span className="text-neutral-600 dark:text-neutral-400">
                                Documents Uploaded
                            </span>
                            <span className="font-medium text-neutral-900 dark:text-neutral-100">
                                {data.documents.length}
                            </span>
                        </div>

                        <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                            <div
                                className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${verificationProgress}%` }}
                            />
                        </div>

                        <div className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-500">
                            {Math.round(verificationProgress)}% Complete
                        </div>
                    </div>
                </div>

                <div className="card p-4 sm:p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-4 sm:mb-5">
                        {isFullyVerified ? (
                            <CheckBadgeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                        ) : (
                            <GiftIcon className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                        )}
                        <span className="font-medium text-sm sm:text-base text-neutral-900 dark:text-neutral-100">
                            {isFullyVerified ? 'Verified Benefits' : 'Potential Benefits'}
                        </span>
                    </div>

                    <div className="space-y-2 sm:space-y-3">
                        <div className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                            {isFullyVerified
                                ? 'You have access to all premium benefits!'
                                : `${missingRequiredDocuments.length} documents needed for verification`
                            }
                        </div>

                        {data.benefits.feeReduction > 0 && (
                            <div className="text-base sm:text-lg font-semibold text-green-600 dark:text-green-400">
                                {data.benefits.feeReduction}% Fee Reduction
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="card p-0 overflow-hidden">
                {/* Tab Navigation - Mobile-friendly horizontal scrolling */}
                <div className="border-b border-neutral-200 dark:border-neutral-700">
                    <div className="overflow-x-auto scrollbar-hide">
                        <nav className="flex space-x-3 sm:space-x-4 md:space-x-6 -mb-px min-w-max" role="tablist">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id as any)}
                                    className={`
                                        flex items-center gap-1 sm:gap-2 px-3 sm:px-5 md:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0
                                        ${activeTab === tab.id
                                            ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                                            : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100'
                                        }
                                    `}
                                >
                                    <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    <span>{tab.label}</span>
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="p-4 sm:p-5 md:p-6">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* Rejected Documents Alert */}
                            {data.documents.some(doc => doc.status === 'rejected') && (
                                <div className="p-4 sm:p-5 md:p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                    <div className="flex items-start gap-3">
                                        <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <h4 className="font-medium text-sm sm:text-base text-red-800 dark:text-red-200 mb-2 sm:mb-3">
                                                Action Required: Delete Rejected Documents
                                            </h4>
                                            <p className="text-xs sm:text-sm text-red-700 dark:text-red-300 mb-3 sm:mb-4">
                                                You have rejected documents that must be deleted before uploading new ones. This prevents duplicate submissions and ensures a clean verification process.
                                            </p>
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                                <div>
                                                    <p className="text-xs sm:text-sm font-medium text-red-700 dark:text-red-300">
                                                        Rejected documents: {data.documents.filter(doc => doc.status === 'rejected').length}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => setActiveTab('documents')}
                                                    className="text-xs sm:text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-2 sm:px-3 sm:py-1.5 rounded-lg transition-colors whitespace-nowrap"
                                                >
                                                    View & Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Missing Documents Alert */}
                            {missingRequiredDocuments.length > 0 && !data.documents.some(doc => doc.status === 'rejected') && (
                                <div className="p-4 sm:p-5 md:p-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                    <h4 className="font-medium text-sm sm:text-base text-yellow-800 dark:text-yellow-200 mb-2 sm:mb-3">
                                        Complete Your Verification
                                    </h4>
                                    <p className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-300 mb-3 sm:mb-4">
                                        Upload the following required documents to get verified:
                                    </p>
                                    <ul className="space-y-1 text-xs sm:text-sm text-yellow-700 dark:text-yellow-300">
                                        {missingRequiredDocuments.map(docType => (
                                            <li key={docType} className="flex items-center gap-2">
                                                <div className="w-1 h-1 bg-yellow-600 rounded-full" />
                                                {DOCUMENT_CONFIGS[docType].displayName}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Next Steps / Actions Required */}
                            {(data as any).nextSteps && (data as any).nextSteps.length > 0 && (
                                <div className="p-4 sm:p-5 md:p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                    <h4 className="font-medium text-sm sm:text-base text-red-800 dark:text-red-200 mb-3 sm:mb-4">
                                        Action Required
                                    </h4>
                                    <div className="space-y-2 sm:space-y-3">
                                        {(data as any).nextSteps.map((step: string, index: number) => (
                                            <div key={index} className="flex items-start gap-3">
                                                <ExclamationTriangleIcon className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                                <p className="text-xs sm:text-sm text-red-700 dark:text-red-300">
                                                    {step}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => setShowUploadModal(true)}
                                        className="mt-4 sm:mt-5 px-4 py-2.5 sm:py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs sm:text-sm font-medium"
                                    >
                                        Upload New Document
                                    </button>
                                </div>
                            )}

                            {/* Document Status Summary */}
                            {(data as any).documentsCount && (
                                <div key={`doc-count-${(data as any).documentsCount.total}-${(data as any).documentsCount.verified}-${(data as any).documentsCount.rejected}`} className="card p-4 sm:p-5 md:p-6">
                                    <h4 className="font-medium text-sm sm:text-base text-neutral-900 dark:text-neutral-100 mb-4 sm:mb-5">
                                        Document Status Summary
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 text-xs sm:text-sm">
                                        <div className="text-center">
                                            <div className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                                                {(data as any).documentsCount.total}
                                            </div>
                                            <div className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">Total</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xl sm:text-2xl font-bold text-green-600">
                                                {(data as any).documentsCount.verified}
                                            </div>
                                            <div className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">Verified</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xl sm:text-2xl font-bold text-yellow-600">
                                                {(data as any).documentsCount.pending}
                                            </div>
                                            <div className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">Pending</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-xl sm:text-2xl font-bold text-red-600">
                                                {(data as any).documentsCount.rejected}
                                            </div>
                                            <div className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">Rejected</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Recent Activity */}
                            <div className="space-y-4 sm:space-y-5">
                                <div>
                                    <h4 className="font-semibold text-sm sm:text-base text-neutral-900 dark:text-neutral-100 mb-4 sm:mb-5">
                                        Recent Activity
                                    </h4>

                                    {/* Combined Activities - Most Recent First */}
                                    {trustHistory.length > 0 || data.documents.length > 0 ? (
                                        <div className="space-y-3 sm:space-y-4">
                                            {/* Trust History Section */}
                                            {trustHistory.length > 0 && (
                                                <div className="space-y-2 sm:space-y-3">
                                                    <h5 className="text-xs sm:text-sm font-medium text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">
                                                        Trust Score Updates
                                                    </h5>
                                                    {trustHistory.slice(0, 3).map((entry, index) => (
                                                        <div key={index} className="flex items-center justify-between p-3 sm:p-4 rounded-lg border border-neutral-200 dark:border-neutral-700">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-1 sm:mb-1.5">
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                                        Trust
                                                                    </span>
                                                                    <span className={`text-xs sm:text-sm font-semibold ${entry.change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                                        {entry.change >= 0 ? '↑' : '↓'} {Math.abs(entry.change)} pts
                                                                    </span>
                                                                </div>
                                                                <p className="text-xs sm:text-sm font-medium text-neutral-900 dark:text-neutral-100">
                                                                    {entry.reason}
                                                                </p>
                                                                <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
                                                                    {new Date(entry.date).toLocaleDateString()} at {new Date(entry.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Document Activity Section */}
                                            {data.documents.length > 0 && (
                                                <div className="space-y-2 sm:space-y-3">
                                                    <h5 className="text-xs sm:text-sm font-medium text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">
                                                        Document Activity
                                                    </h5>
                                                    {data.documents.slice(0, 5).map((doc) => {
                                                        const formattedDoc = formatDocument(doc);
                                                        return (
                                                            <div key={doc.id} className="flex items-start justify-between p-3 sm:p-4 rounded-lg border border-neutral-200 dark:border-neutral-700 gap-2 sm:gap-3">
                                                                <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                                                                    <DocumentArrowUpIcon className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-400 flex-shrink-0 mt-0.5" />
                                                                    <div className="min-w-0">
                                                                        <div className="flex items-center gap-2 mb-1 sm:mb-1.5">
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                                                                Document
                                                                            </span>
                                                                            <VerificationStatusChip status={doc.status} size="sm" />
                                                                        </div>
                                                                        <p className="text-xs sm:text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                                                                            {formattedDoc.displayName}
                                                                        </p>
                                                                        <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
                                                                            Uploaded {formattedDoc.uploadedDate}
                                                                        </p>
                                                                    </div>
                                                                </div>

                                                                {doc.status === 'rejected' && (
                                                                    <button
                                                                        onClick={() => setActiveTab('documents')}
                                                                        className="text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 whitespace-nowrap flex-shrink-0 py-1 px-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                                                        title="Go to documents tab to delete this document"
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* View All Activity Link */}
                                            {(trustHistory.length > 3 || data.documents.length > 5) && (
                                                <button
                                                    onClick={() => setActiveTab('history')}
                                                    className="w-full py-2 px-3 text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                >
                                                    View all activity →
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 text-center py-4 px-3 rounded-lg border border-neutral-200 dark:border-neutral-700">
                                            No activity yet
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Documents Tab */}
                    {activeTab === 'documents' && (
                        <div className="space-y-3 sm:space-y-4">
                            {data.documents.length > 0 ? (
                                <div className="space-y-2 sm:space-y-3">
                                    {data.documents.map((doc) => {
                                        const formattedDoc = formatDocument(doc);
                                        return (
                                            <div key={doc.id} className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 border rounded-lg ${doc.status === 'rejected'
                                                ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10'
                                                : 'border-neutral-200 dark:border-neutral-700'
                                                }`}>
                                                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                                    <DocumentArrowUpIcon className="w-5 h-5 text-neutral-600 dark:text-neutral-400 flex-shrink-0" />
                                                    <div className="min-w-0">
                                                        <div className="font-medium text-xs sm:text-sm text-neutral-900 dark:text-neutral-100 truncate">
                                                            {formattedDoc.displayName}
                                                        </div>
                                                        <div className="text-xs text-neutral-600 dark:text-neutral-400">
                                                            {formattedDoc.fileSizeMB} • Uploaded {formattedDoc.uploadedDate}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <VerificationStatusChip status={doc.status} />

                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            className={`hidden md:block p-1.5 text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 transition-colors ${loadingDocument === doc.id ? 'opacity-50 cursor-not-allowed' : ''
                                                                }`}
                                                            title="View document"
                                                            onClick={() => handleViewDocument(doc)}
                                                            disabled={loadingDocument === doc.id}
                                                        >
                                                            {loadingDocument === doc.id ? (
                                                                <div className="w-4 h-4 border-2 border-neutral-300 border-t-neutral-600 rounded-full animate-spin" />
                                                            ) : (
                                                                <EyeIcon className="w-4 h-4" />
                                                            )}
                                                        </button>

                                                        {['rejected', 'expired'].includes(doc.status) && (
                                                            <button
                                                                className={`${doc.status === 'rejected' ? 'px-2 sm:px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-xs sm:text-sm whitespace-nowrap' : 'p-1.5'} transition-colors ${deletingDocument === doc.id
                                                                    ? 'opacity-50 cursor-not-allowed'
                                                                    : doc.status === 'rejected' ? '' : 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
                                                                    }`}
                                                                title={doc.status === 'rejected' ? 'Delete rejected document (required before uploading new documents)' : 'Delete document'}
                                                                onClick={() => handleDeleteDocument(doc)}
                                                                disabled={deletingDocument === doc.id}
                                                            >
                                                                {doc.status === 'rejected' ? (
                                                                    <>
                                                                        <TrashIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 inline" />
                                                                        <span className="hidden sm:inline">Delete Required</span>
                                                                        <span className="sm:hidden">Delete</span>
                                                                    </>
                                                                ) : (
                                                                    <TrashIcon className="w-4 h-4" />
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-8 sm:py-10 md:py-12">
                                    <DocumentArrowUpIcon className="w-10 h-10 sm:w-12 sm:h-12 text-neutral-400 mx-auto mb-3 sm:mb-4" />
                                    <h4 className="text-base sm:text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
                                        No Documents Uploaded
                                    </h4>
                                    <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 mb-4 sm:mb-6">
                                        Start by uploading your verification documents
                                    </p>
                                    <button
                                        onClick={() => setShowUploadModal(true)}
                                        disabled={data.documents.some(doc => doc.status === 'rejected')}
                                        className={`${data.documents.some(doc => doc.status === 'rejected')
                                            ? 'btn-secondary opacity-50 cursor-not-allowed'
                                            : 'btn-primary'
                                            }`}
                                        title={data.documents.some(doc => doc.status === 'rejected')
                                            ? 'Please delete rejected documents first'
                                            : 'Upload your first document'
                                        }
                                    >
                                        Upload First Document
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Benefits Tab */}
                    {activeTab === 'benefits' && (
                        <VerificationBenefits
                            benefits={data.benefits}
                            isVerified={isFullyVerified}
                            trustScore={data.trustSystem.currentScore}
                        />
                    )}

                    {/* History Tab */}
                    {activeTab === 'history' && (
                        <div className="space-y-2 sm:space-y-3">
                            {trustHistory.map((entry, index) => (
                                <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 sm:p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg">
                                    <div className="min-w-0">
                                        <div className="font-medium text-xs sm:text-sm text-neutral-900 dark:text-neutral-100">
                                            {entry.reason}
                                        </div>
                                        <div className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                                            {new Date(entry.date).toLocaleString()}
                                        </div>
                                    </div>

                                    <div className="text-left sm:text-right flex-shrink-0">
                                        <div className={`font-medium text-xs sm:text-sm ${entry.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {entry.change >= 0 ? '+' : ''}{entry.change}
                                        </div>
                                        <div className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                                            Score: {entry.score}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {trustHistory.length === 0 && (
                                <div className="text-center py-8 text-xs sm:text-sm text-neutral-600 dark:text-neutral-400">
                                    No trust score history yet
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            {deleteConfirm.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-800 rounded-lg p-4 sm:p-6 max-w-md w-full">
                        <h3 className="text-base sm:text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-3 sm:mb-4">
                            Delete Document
                        </h3>

                        <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 mb-5 sm:mb-6">
                            Are you sure you want to delete "{deleteConfirm.documentName}"? This action cannot be undone.
                        </p>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={cancelDeleteDocument}
                                className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-neutral-600 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors"
                                disabled={deletingDocument !== null}
                            >
                                Cancel
                            </button>

                            <button
                                onClick={confirmDeleteDocument}
                                className={`px-3 sm:px-4 py-2 rounded-lg text-white font-medium text-xs sm:text-sm transition-colors ${deletingDocument !== null
                                    ? 'bg-red-400 cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-700'
                                    }`}
                                disabled={deletingDocument !== null}
                            >
                                {deletingDocument !== null ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Modal */}
            <DocumentUploadModal
                isOpen={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                onUploadComplete={() => {
                    refresh();
                }}
                allowedDocumentTypes={missingRequiredDocuments.length > 0 ? missingRequiredDocuments : undefined}
                hasRejectedDocuments={data.documents.some(doc => doc.status === 'rejected')}
                rejectedDocumentsCount={data.documents.filter(doc => doc.status === 'rejected').length}
            />

            {/* Document Viewer */}
            <DocumentViewer
                document={documentViewer.document}
                isOpen={documentViewer.isOpen}
                onClose={handleCloseDocumentViewer}
                onDownload={handleDownloadDocument}
            />
        </div>
    );
};

export default VerificationDashboard;